import dayjs from "dayjs";

/**
 * Pure utility function to calculate an amortization schedule
 * @param {Object} params - Loan parameters
 * @param {string} params.inception_date - Start date of the loan (MM/DD/YYYY or YYYY-MM-DD)
 * @param {string} params.maturity_date - End date of the loan
 * @param {number} params.interest_rate - Annual interest rate (as decimal, e.g., 0.05 for 5%)
 * @param {string} params.payment_frequency - Payment frequency (Weekly, Bi-weekly, Monthly, Quarterly, Semi-Annual, Yearly)
 * @param {number} params.amount - Principal amount to amortize
 * @param {number} [params.balloon_amount=0] - Balloon payment amount at maturity
 * @param {number} [params.extra_principal_rate=0] - Extra principal payment rate (as decimal)
 * @param {string} [params.day_count_convention="30/360"] - Day count convention (Actual/365 or 30/360)
 * @param {number} [params.interest_only_periods=0] - Number of interest-only periods
 * @returns {Array} Array of amortization records
 */
export const calculateAmortizationSchedule = (params) => {
  const {
    inception_date,
    maturity_date,
    interest_rate,
    payment_frequency,
    amount,
    balloon_amount = 0,
    extra_principal_rate = 0,
    day_count_convention = "30/360",
    interest_only_periods = 0,
  } = params;

  // Validate required parameters
  if (!inception_date) throw new Error("Inception date is required");
  if (!maturity_date) throw new Error("Maturity date is required");
  if (interest_rate === undefined || interest_rate === null) throw new Error("Interest rate is required");
  if (!payment_frequency) throw new Error("Payment frequency is required");
  if (!amount || amount <= 0) throw new Error("Amount must be greater than 0");

  // Determine payments per year and date increment logic
  let paymentsPerYear = 12;
  let addAmount = 1;
  let addUnit = "month";

  if (payment_frequency === "Quarterly") {
    paymentsPerYear = 4;
    addAmount = 3;
    addUnit = "month";
  } else if (payment_frequency === "Annual") {
    paymentsPerYear = 1;
    addAmount = 1;
    addUnit = "year";
  } else if (payment_frequency === "Bi-weekly") {
    paymentsPerYear = 26;
    addAmount = 2;
    addUnit = "week";
  } else if (payment_frequency === "Weekly") {
    paymentsPerYear = 52;
    addAmount = 1;
    addUnit = "week";
  } else if (payment_frequency === "Semi-Annual") {
    paymentsPerYear = 2;
    addAmount = 6;
    addUnit = "month";
  }

  const periodicRate = interest_rate / paymentsPerYear;

  // Calculate total number of payments
  let totalPayments = 0;
  const start = dayjs(inception_date);
  const end = dayjs(maturity_date);

  // Validate dates
  if (!start.isValid()) {
    throw new Error("Invalid inception date");
  }
  if (!end.isValid()) {
    throw new Error("Invalid maturity date");
  }

  if (end.isBefore(start) || end.isSame(start)) {
    throw new Error("Maturity date must be after inception date");
  }

  const diffMonths = end.diff(start, "month", true);
  const roundedDiff = Math.round(diffMonths * 10000) / 10000;
  totalPayments = Math.ceil(roundedDiff * (paymentsPerYear / 12));

  const principal = parseFloat(amount);
  const balloon = parseFloat(balloon_amount || 0);
  const ioPeriods = parseInt(interest_only_periods || 0, 10);
  const extraPrincipalRate = parseFloat(extra_principal_rate || 0);

  // Calculate Amortization Payment
  const amortizingPaymentsCount = Math.max(0, totalPayments - ioPeriods);

  let amortizedPaymentAmount = 0;

  if (amortizingPaymentsCount > 0) {
    if (periodicRate > 0) {
      // PMT = (PV - FV / (1+r)^n) / ((1 - (1+r)^-n) / r)
      const pv = principal;
      const fv = balloon;
      const n = amortizingPaymentsCount;
      const r = periodicRate;

      // Present Value of the Balloon payment
      const pvBalloon = fv / Math.pow(1 + r, n);

      // Principal part to be amortized by regular payments
      const amountToAmortize = pv - pvBalloon;

      // Standard annuity formula for the amortizable amount
      amortizedPaymentAmount = (amountToAmortize * r) / (1 - Math.pow(1 + r, -n));
    } else {
      // 0% interest
      amortizedPaymentAmount = (principal - balloon) / amortizingPaymentsCount;
    }
  }

  const schedule = [];
  let currentBalance = principal;
  let cumulativePrincipal = 0;
  let cumulativeInterest = 0;

  // Start date logic
  let prevDate = dayjs(inception_date);
  let currentDate = prevDate;

  for (let i = 1; i <= totalPayments; i++) {
    // Determine the date of THIS payment
    currentDate = prevDate.add(addAmount, addUnit);

    const isMaturityReached = currentDate.isSame(end, "day") || currentDate.isAfter(end, "day");
    const isLastPayment = i === totalPayments || isMaturityReached;

    const isInterestOnly = i <= ioPeriods;

    // Calculate interest for this period
    let interestPayment = 0;

    if (day_count_convention === "Actual/365") {
      const daysInPeriod = currentDate.diff(prevDate, "day");
      interestPayment = currentBalance * interest_rate * (daysInPeriod / 365);
    } else {
      // Default / 30/360 behavior (simplified)
      interestPayment = currentBalance * periodicRate;
    }

    // Calculate principal for this period
    let principalPayment = 0;
    let monthlyPayment = 0;

    if (isInterestOnly) {
      principalPayment = 0;
      monthlyPayment = interestPayment;
    } else {
      principalPayment = amortizedPaymentAmount - interestPayment;
      monthlyPayment = amortizedPaymentAmount;
    }

    // Handle final payment to match Balloon
    if (isLastPayment) {
      const expectedPrincipalPayment = currentBalance - balloon;
      principalPayment = expectedPrincipalPayment;
      monthlyPayment = principalPayment + interestPayment;
    }

    // Calculate extra principal payment
    const extraPrincipal = extraPrincipalRate > 0 ? currentBalance * extraPrincipalRate : 0;

    // Update balances
    const beginningBalance = currentBalance;
    currentBalance -= principalPayment;
    currentBalance -= extraPrincipal; // Apply extra principal

    // Check if loan is paid off early (balance goes to or below balloon amount)
    const isPaidOff = currentBalance <= balloon;
    if (isPaidOff && currentBalance < balloon) {
      // Adjust to ensure we don't overpay - cap at balloon amount
      const overpayment = balloon - currentBalance;
      currentBalance = balloon;
      // Reduce the extra principal by the overpayment amount
      // Note: This handles edge case where last payment would go below balloon
    }

    cumulativePrincipal += principalPayment + extraPrincipal;
    cumulativeInterest += interestPayment;

    schedule.push({
      payment_number: i,
      payment_date: currentDate.format("YYYY-MM-DD"),
      beginning_balance: parseFloat(beginningBalance.toFixed(2)),
      payment_amount: parseFloat(monthlyPayment.toFixed(2)),
      principal_payment: parseFloat(principalPayment.toFixed(2)),
      interest_payment: parseFloat(interestPayment.toFixed(2)),
      ending_balance: parseFloat(Math.max(0, currentBalance).toFixed(2)),
      cumulative_principal: parseFloat(cumulativePrincipal.toFixed(2)),
      cumulative_interest: parseFloat(cumulativeInterest.toFixed(2)),
      extra_principal_payment: parseFloat(extraPrincipal.toFixed(2)),
      is_actual: false,
      actual_payment_date: null,
      actual_payment_amount: null,
      extra_interest_payment: null,
      notes: null,
    });

    // Advance prevDate for next iteration
    prevDate = currentDate;

    // Break if loan is fully paid off (reached balloon amount or zero)
    if (isLastPayment || isPaidOff) break;
  }

  // Add balloon payment row if there's a balloon amount
  if (balloon > 0 && schedule.length > 0) {
    const lastPayment = schedule[schedule.length - 1];
    const balloonBalance = lastPayment.ending_balance;

    // Only add balloon payment if there's still a balance
    if (balloonBalance > 0) {
      const balloonPaymentNumber = lastPayment.payment_number + 1;
      const balloonDate = dayjs(lastPayment.payment_date);

      // Calculate interest for the balloon payment (typically same date, so minimal interest)
      let balloonInterest = 0;
      if (day_count_convention === "Actual/365") {
        // Same day payment, so no additional interest typically
        balloonInterest = 0;
      } else {
        balloonInterest = 0;
      }

      const balloonPrincipal = balloonBalance;
      const balloonPaymentAmount = balloonPrincipal + balloonInterest;

      cumulativePrincipal += balloonPrincipal;
      cumulativeInterest += balloonInterest;

      schedule.push({
        payment_number: balloonPaymentNumber,
        payment_date: balloonDate.format("YYYY-MM-DD"),
        beginning_balance: parseFloat(balloonBalance.toFixed(2)),
        payment_amount: parseFloat(balloonPaymentAmount.toFixed(2)),
        principal_payment: parseFloat(balloonPrincipal.toFixed(2)),
        interest_payment: parseFloat(balloonInterest.toFixed(2)),
        ending_balance: 0.0,
        cumulative_principal: parseFloat(cumulativePrincipal.toFixed(2)),
        cumulative_interest: parseFloat(cumulativeInterest.toFixed(2)),
        extra_principal_payment: 0.0,
        is_actual: false,
        actual_payment_date: null,
        actual_payment_amount: null,
        extra_interest_payment: null,
        notes: "Balloon Payment",
      });
    }
  }

  return schedule;
};
